import { AdminField, ResourceConfig } from './schema'

type FormValues = Record<string, unknown>

export interface SongRow {
    title: string
    url: string
}

/**
 * Extract a YouTube video ID from a full URL or return the input if it already
 * looks like a bare ID. Supports watch?v=, youtu.be/, embed/ and shorts/.
 */
export function extractYouTubeId(input: string): string {
    const value = (input ?? '').trim()
    if (!value) return ''
    const patterns = [
        /[?&]v=([a-zA-Z0-9_-]{11})/,
        /youtu\.be\/([a-zA-Z0-9_-]{11})/,
        /\/embed\/([a-zA-Z0-9_-]{11})/,
        /\/shorts\/([a-zA-Z0-9_-]{11})/
    ]
    for (const p of patterns) {
        const m = value.match(p)
        if (m) return m[1]
    }
    // Already a bare 11-char id
    if (/^[a-zA-Z0-9_-]{11}$/.test(value)) return value
    return value
}

export function youtubeThumbnail(videoId: string): string {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}

/** Convert an API record into editable form values for the given config. */
export function recordToForm(
    config: ResourceConfig,
    record?: Record<string, unknown>
): FormValues {
    const values: FormValues = {}
    for (const field of config.fields) {
        const raw = record?.[field.name]
        switch (field.type) {
            case 'tags':
            case 'ageGroups':
                values[field.name] = Array.isArray(raw) ? (raw as string[]) : []
                break
            case 'verse': {
                const v = (raw as { text?: string; reference?: string }) ?? {}
                values[`${field.name}__text`] = v.text ?? ''
                values[`${field.name}__reference`] = v.reference ?? ''
                break
            }
            case 'songs': {
                const list = Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
                values[field.name] = list.map((s) => ({
                    title: String(s.title ?? s.name ?? ''),
                    url: String(s.url ?? s.audioUrl ?? '')
                }))
                break
            }
            case 'number':
                values[field.name] = raw == null ? '' : String(raw)
                break
            default:
                values[field.name] = raw == null ? '' : String(raw)
        }
    }
    return values
}

/**
 * Convert form values into a request payload. Empty optional fields are
 * dropped so PATCH only sends what changed-or-was-entered.
 */
export function formToPayload(
    config: ResourceConfig,
    values: FormValues
): Record<string, unknown> {
    const payload: Record<string, unknown> = {}
    const put = (key: string, val: unknown, field: AdminField) => {
        const isEmpty =
            val === '' ||
            val === undefined ||
            val === null ||
            (Array.isArray(val) && val.length === 0)
        if (isEmpty) {
            if (field.required) payload[key] = val
            return
        }
        payload[key] = val
    }

    for (const field of config.fields) {
        switch (field.type) {
            case 'tags':
            case 'ageGroups': {
                const raw = values[field.name]
                const list = Array.isArray(raw)
                    ? raw
                    : String(raw ?? '')
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                put(field.name, list, field)
                break
            }
            case 'number': {
                const raw = values[field.name]
                if (raw === '' || raw === undefined || raw === null) {
                    if (field.required) payload[field.name] = raw
                    break
                }
                const n = Number(raw)
                if (!Number.isNaN(n)) payload[field.name] = n
                break
            }
            case 'youtube': {
                const id = extractYouTubeId(String(values[field.name] ?? ''))
                put(field.name, id, field)
                break
            }
            case 'verse': {
                const text = String(values[`${field.name}__text`] ?? '').trim()
                const reference = String(values[`${field.name}__reference`] ?? '').trim()
                if (text || reference) payload[field.name] = { text, reference }
                break
            }
            case 'songs': {
                const raw = Array.isArray(values[field.name])
                    ? (values[field.name] as SongRow[])
                    : []
                const songs = raw
                    .map((s) => ({
                        title: (s.title ?? '').trim(),
                        url: (s.url ?? '').trim()
                    }))
                    .filter((s) => s.title || s.url)
                if (songs.length) payload[field.name] = songs
                break
            }
            default:
                put(field.name, values[field.name], field)
        }
    }

    if (config.resource === 'videos' && !payload.thumbnail) {
        const videoId = String(payload.videoId ?? '').trim()
        if (/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
            payload.thumbnail = youtubeThumbnail(videoId)
        }
    }

    return payload
}
